/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useContext, useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {KTIcon} from '../../../helpers'
import {Dropdown1} from '../../content/dropdown/Dropdown1'
import {getCSS, getCSSVariableValue} from '../../../assets/ts/_utils'
import {useThemeMode} from '../../layout/theme-mode/ThemeModeProvider'
import { BLACKBOXES_MAX_TEMP_URL,BLACKBOXES_AVG_TEMP_URL } from '../../../../urls'
import {NO_DATA_TEXT, chartValueFormatter, hasAnyValue, toChartSeries} from '../../../helpers/ChartDataHelpers'
import { blackboxIdContext } from '../../../../app/modules/profile/components/contexts/BlackboxIdContext'
import { useAuth } from '../../../../app/modules/auth'
type Props = {
  className: string
}

/**
 * Oturumun düştüğünü taşıyan işaret.
 *
 * `Promise.all` içindeki iki istekten biri 401 alırsa `logout()` **bir kez** çağrılmalı
 * ve bu durum sıradan bir hatadan ayrılmalı -- "oturum düştü" ile "sunucu hata verdi"
 * kullanıcı için ayrı şeyler.
 */
const UNAUTHORIZED = Symbol('unauthorized')

const BlackboxTempGraph: React.FC<Props> = ({className}) => {
  const blackboxContext = useContext(blackboxIdContext)
  const chartRef = useRef<HTMLDivElement | null>(null)
  const [tempList, setTempList] = useState<any>([])
  const [maxTempList, setmaxTempList] = useState<any>([])
  /**
   * Veri alinamadi mi.
   *
   * `loading` ve `loading1` KALDIRILDI. Ikisi de yalnizca useEffect'in bagimlilik
   * dizisinde kullaniliyordu ve istekleri tekrar tetikliyorlardi (bkz. asagidaki
   * veri cekme effect'i); ekranda hic okunmuyorlardi.
   */
  const [failed, setFailed] = useState(false)
  const {mode} = useThemeMode()
  const { currentUser, logout } = useAuth()
  const token = currentUser?.token

  const createHeader = () => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Authorization', "" + token?.toString());
    return headers
  }

  /**
   * 1) VERİ ÇEKME.
   *
   * ## Düzeltilen hata: her uca 3 kez istek
   *
   * Önceki hali tek bir effect'ti ve bağımlılık dizisi `[chartRef, mode, loading, loading1]`
   * idi. İki istek **koşulsuz** atılıyordu ve tamamlandıklarında `setLoading(true)` /
   * `setLoading1(true)` çağırıyorlardı -- yani effect'in kendi bağımlılıklarını
   * yazıyorlardı. Sonuç:
   *
   * - 1. koşu (mount): A, B gönderildi
   * - A bitti → `loading` false→true → effect tekrar koştu → A, B **yine** gönderildi
   * - B bitti → `loading1` false→true → effect tekrar koştu → A, B **yine** gönderildi
   * - sonrakiler `true→true` olduğu için React güncellemeyi atlıyor ve döngü duruyor
   *
   * Yani her açılışta 2 yerine **6 istek** gidiyor, grafik 3 kez kurulup yıkılıyordu.
   * Ayrıca `setTimeout(() => setLoading(true), 5000)` hiç temizlenmiyordu ve
   * `console.log("temo")` her koşuda yazıyordu.
   */
  useEffect(() => {
    const blackboxId = blackboxContext[0]
    if (!blackboxId) {
      return
    }
    let cancelled = false

    const load = async () => {
      try {
        const [avg, max] = await Promise.all([
          fetchTemps(BLACKBOXES_AVG_TEMP_URL, blackboxId),
          fetchTemps(BLACKBOXES_MAX_TEMP_URL, blackboxId),
        ])
        if (cancelled) {
          return
        }
        setTempList(avg)
        setmaxTempList(max)
        setFailed(false)
      } catch (error) {
        if (error === UNAUTHORIZED) {
          logout()
          return
        }
        console.error('Cihaz sicakligi verisi alinamadi:', error)
        if (!cancelled) {
          // Eski veriyi EKRANDA BIRAKMIYORUZ: yenisi alinamadiginda eskisini
          // gostermeye devam etmek "su an boyle" izlenimi verir.
          setTempList([])
          setmaxTempList([])
          setFailed(true)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blackboxContext[0]])

  /**
   * 2) GRAFİK ÇİZME. Yalnızca tema ya da veri değiştiğinde.
   *
   * "Ölçüm yok" durumu `getChartOptions` içinde ele alınıyor (`NO_DATA_TEXT`), o yüzden
   * boş veri için burada ayrı bir dal yok.
   */
  useEffect(() => {
    const chart = refreshChart()
    return () => {
      if (chart) {
        chart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tempList, maxTempList])

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(chartRef.current, getChartOptions(height,tempList,maxTempList))
    if (chart) {
      chart.render()
    }

    return chart
  }
  /**
   * Sıcaklık verisini çeker.
   *
   * Önceden iki ayrı fonksiyon vardı ve ikisinde de `response.ok`, 401 ve `catch`
   * **hiç yoktu**: `res.json()` hata gövdesini ayrıştırıp state'e yazıyordu, grafik de
   * onu veri sanıyordu.
   */
  async function fetchTemps(url: string, blackboxId: string) {
    const response = await fetch(url + `?blackboxId=${blackboxId}`, {
      method: 'GET',
      headers: createHeader(),
    })
    if (response.status === 401) {
      throw UNAUTHORIZED
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    return response.json()
  }
  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5'>
        {/* begin::Title */}
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Cihaz Sıcaklığı</span>

          <span className='text-muted fw-semibold fs-7'>Son 5 gün</span>
        </h3>
        {/* end::Title */}

        {/* begin::Toolbar */}
        <div className='card-toolbar'>
          {/* begin::Menu */}
          <button
            type='button'
            className='btn btn-sm btn-icon btn-color-primary btn-active-light-primary'
            data-kt-menu-trigger='click'
            data-kt-menu-placement='bottom-end'
            data-kt-menu-flip='top-end'
          >
            <KTIcon iconName='category' className='fs-2' />
          </button>
          <Dropdown1 />
          {/* end::Menu */}
        </div>
        {/* end::Toolbar */}
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className='card-body'>
        {/* Hata artik GORUNUYOR. Oncesinde iki fetch'te de hata denetimi yoktu ve
            basarisizlik "olcum yok" gibi gorunuyordu -- yani veri olmadigini mi, veriyi
            alamadigimizi mi bilmek mumkun degildi. */}
        {failed && <div className='text-danger mb-3'>Cihaz sıcaklığı verisi alınamadı.</div>}
        {/* begin::Chart */}
        <div ref={chartRef} id='kt_charts_widget_1_chart' style={{height: '350px'}} />
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export {BlackboxTempGraph}

function getChartOptions(height: number,data:any,maxTempList:any): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-primary')
  const secondaryColor = getCSSVariableValue('--bs-gray-300')

  // Hicbir gun icin olcum yoksa seriyi bos gonderiyoruz; ApexCharts o zaman
  // bos bir grafik yerine noData mesajini gosteriyor (M-19 devami).
  const avg = toChartSeries(data["temp"])
  const max = toChartSeries(maxTempList["temp"])
  const anyData = hasAnyValue(data["temp"]) || hasAnyValue(maxTempList["temp"])

  return {
    noData: {
      text: NO_DATA_TEXT,
      align: 'center',
      verticalAlign: 'middle',
      style: {color: labelColor, fontSize: '14px'},
    },
    series: anyData
      ? [
          {name: 'Ortalama Sıcaklık', data: avg},
          {name: 'Max Sıcaklık', data: max},
        ]
      : [],
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height: height,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '30%',
        borderRadius: 5,
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: data["date"],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '10px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        // Olcum yoksa deger yerine "Olcum yok" yaziyor; onceden "null °C" cikiyordu.
        formatter: chartValueFormatter(' °C'),
      },
    },
    colors: [baseColor, secondaryColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}
